import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, useTheme, useMediaQuery, Grid } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

// Components
import Sidebar from './Sidebar';
import ChatWindow from './ChatWindow';

// Services and APIs
import AdminMessagingAPIs from '../../utilities/api/IMS/AdminMessagingAPIs';
import WebSocketService from '../../utilities/api/IMS/websocketService';

// Utilities
import {
  notifyError,
  notifyInfo,
  notifySuccess,
} from '../../utils/notifyToasts';

// Redux actions
import {
  addMessageToConversation,
  updateConversationLastMessage,
} from '../../redux/slices/conversationSlice';
import PageTransition from '../PageTransition/PageTransition';

/**
 * MessagingPage Component
 *
 * Main messaging interface that provides real-time chat functionality.
 *
 * Key Features:
 * - Real-time messaging via WebSocket connection
 * - Chat request management (accept/decline invitations)
 * - Message sending and receiving with duplicate prevention
 * - Conversation state management via Redux
 * - Navigation state tracking
 *
 * WebSocket Message Types Handled:
 * - 'new_message': Regular chat messages between users
 * - 'group_invitation': Incoming chat/group invitations
 * - 'group_invitation_response': Responses to sent invitations
 */
const MessagingPage = () => {
  // ========== REDUX STATE ==========
  const { user, access } = useSelector(state => state.user.user);
  const { selectedConversationId, selectedConversation } = useSelector(
    state => state.conversation
  );
  const dispatch = useDispatch();

  // ========== NAVIGATION STATE ==========
  const location = useLocation();

  // ================= RESPONSIVE =================
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ================= STATE =================
  const [lastLocation, setLastLocation] = useState('/');

  // ========== MESSAGING STATE ==========
  const [pendingMessages, setPendingMessages] = useState([]); // Track messages being sent
  const [lastSentMessageId, setLastSentMessageId] = useState(null); // Prevent message duplication
  const [lastSentMessageText, setLastSentMessageText] = useState(''); // Prevent message duplication
  const [chatRequests, setChatRequests] = useState([]); // Incoming chat invitations
  const [isLoadingThreads, setIsLoadingThreads] = useState(false); // Loading state for threads

  // ========== NAVIGATION TRACKING ==========
  /**
   * Track the previous location for navigation purposes
   * Useful for "back" functionality or breadcrumbs
   */
  useEffect(() => {
    if (location.state?.from) {
      setLastLocation(location.state.from);
    }
  }, [location.state]);

  // ========== INITIALIZATION ==========
  /**
   * Initialize component on mount
   * Fetches pending chat requests from the server
   */
  useEffect(() => {
    getChatRequests();
  }, []);

  // ========== WEBSOCKET CONNECTION MANAGEMENT ==========
  /**
   * Manages WebSocket connection lifecycle
   *
   * Connection Logic:
   * - If a conversation is selected: connect to that conversation's room
   * - If no conversation: connect to user's personal room
   * - Automatically reconnects when conversation changes
   * - Cleans up connection on component unmount
   */
  useEffect(() => {
    const connectionRoom = selectedConversation
      ? selectedConversation.userId
      : user.id;

    // Establish WebSocket connection
    WebSocketService.connect(access, connectionRoom);
    WebSocketService.onMessage(handleIncomingWsResponse);

    // Cleanup function: disconnect when component unmounts or dependencies change
    return () => {
      WebSocketService.disconnect();
    };
  }, [selectedConversation, access]);

  // ========== CHAT REQUESTS API ==========
  /**
   * Fetches all pending chat requests from the server
   * Updates local state with received requests
   */
  const getChatRequests = async () => {
    try {
      const requests = await AdminMessagingAPIs.GetChatRequests();
      setChatRequests(requests);
    } catch (error) {
      console.error('Error fetching chat requests:', error);
    }
  };

  /**
   * Handles accept/decline actions for chat requests
   *
   * @param {string} id - The chat request ID
   * @param {string} response - Either 'accept' or 'decline'
   *
   * Flow:
   * 1. Send accept/decline request to server
   * 2. Show success notification to user
   * 3. If accepted: Initialize conversation with empty message
   * 4. Refresh chat requests list
   */
  const acceptRejectRequest = async (id, response) => {
    try {
      const res = await AdminMessagingAPIs.AcceptDeclineChatRequest(id, {
        action: response,
      });

      // Show appropriate success message
      const message =
        response === 'accept'
          ? 'Chat request accepted successfully'
          : 'Chat request declined successfully';
      notifySuccess(message);

      // Initialize conversation if request was accepted
      if (response === 'accept') {
        const msgId = uuidv4();
        const timestamp = new Date().toLocaleString([], {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });

        // Add empty initial message to conversation
        dispatch(
          addMessageToConversation({
            conversationId: id,
            message: {
              id: msgId,
              sender: res.thread_name,
              text: '',
              timestamp: timestamp,
              isCurrentUser: true,
            },
          })
        );

        // Update conversation's last message metadata
        dispatch(
          updateConversationLastMessage({
            id: id,
            message: '',
            time: timestamp,
            username: res.thread_name,
            name: res.thread_name,
            userId: res.thread_user_id,
          })
        );
      }
    } catch (error) {
      console.error('Error processing chat request:', error);
      notifyError('Error accepting/declining chat request');
    } finally {
      // Always refresh the requests list after processing
      await getChatRequests();
    }
  };

  // ========== WEBSOCKET MESSAGE HANDLERS ==========
  /**
   * Handles incoming regular chat messages
   *
   * @param {Object} messageData - WebSocket message payload
   *
   * Message Processing:
   * 1. Prevents duplicate message processing using lastSentMessageText
   * 2. Formats incoming message with proper timestamp and sender info
   * 3. Updates Redux store with new message
   * 4. Updates conversation's last message for UI display
   */
  const handleIncomingMessage = useCallback(
    messageData => {
      // Prevent processing duplicate messages (especially our own echoed messages)
      if (messageData.message === lastSentMessageText) {
        return;
      }

      if (messageData && messageData.message) {
        // Format the incoming message for our UI
        const incomingMessage = {
          id: messageData.message_id,
          text: messageData.message,
          date: new Date(messageData.timestamp).toLocaleString([], {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }),
          sender: messageData.sender,
          isCurrentUser: messageData.sender === user.name, // Check if message is from current user
        };

        // Update the conversation's last message info (for sidebar display)
        dispatch(
          updateConversationLastMessage({
            id: messageData.thread_id,
            message: messageData.message,
            time: incomingMessage.date,
          })
        );

        // Add the message to the conversation thread
        dispatch(
          addMessageToConversation({
            conversationId: messageData.thread_id,
            message: incomingMessage,
          })
        );
      }
    },
    [selectedConversation, lastSentMessageText, user.name, dispatch]
  );

  /**
   * Handles incoming chat/group invitations
   *
   * @param {Object} messageData - WebSocket invitation payload
   *
   * Invitation Processing:
   * 1. Formats invitation data for local state
   * 2. Adds to chat requests list
   * 3. Shows notification to user
   */
  const handleIncomingInvitation = messageData => {
    // Format invitation data for our UI
    const newChatRequest = {
      created_at: new Date(messageData.created_at).toLocaleString([], {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }),
      expires_at: new Date(messageData.expires_at).toLocaleString([], {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }),
      id: messageData.invitation_id,
      group_name: messageData.group_name,
      group_description: messageData.group_description,
      invited_by_username: messageData.invited_by,
      thread_id: messageData.thread_id,
      message: messageData.message,
      status: messageData.status,
    };

    // Add to local chat requests for user to accept/decline
    setChatRequests(prevRequests => [...prevRequests, newChatRequest]);

    // Show notification to user
    const notificationMessage = messageData.group_name
      ? `You have a chat request from ${messageData.invited_by} for joining ${messageData.group_name}`
      : `You have a chat request from ${messageData.invited_by}`;
    notifyInfo(notificationMessage);
  };

  /**
   * Handles responses to invitations we sent to others
   *
   * @param {Object} messageData - WebSocket invitation response payload
   *
   * Response Processing:
   * 1. If accepted: Initialize conversation with placeholder message
   * 2. Show notification about acceptance/rejection
   * 3. Remove processed request from local state
   */
  const handleIncomingInvitationResponse = messageData => {
    if (messageData.response === 'accepted') {
      // Initialize accepted conversation with placeholder message
      dispatch(
        addMessageToConversation({
          message: {
            id: messageData.message_id,
            text: '',
            date: new Date(messageData.responded_at),
            sender: '',
            isCurrentUser: false,
          },
        })
      );

      // Update conversation metadata
      dispatch(
        updateConversationLastMessage({
          id: messageData.thread_id,
          username: messageData.group_name
            ? messageData.group_name
            : messageData.responded_by,
          time: new Date(messageData.responded_at).toLocaleString([], {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }),
          name: messageData.group_name
            ? messageData.group_name
            : messageData.responded_by,
          userId: messageData.thread_user_id,
        })
      );
    }

    // Show notification about the response
    const action =
      messageData.response === 'accepted' ? 'accepted' : 'rejected';
    const notificationMessage = messageData.group_name
      ? `${messageData.responded_by} has ${action} your chat request for joining ${messageData.group_name}`
      : `${messageData.responded_by} has ${action} your chat request`;
    notifyInfo(notificationMessage);

    // Remove the processed request from our local state
    setChatRequests(prevRequests =>
      prevRequests.filter(request => request.id !== messageData.invitation_id)
    );
  };

  /**
   * Main WebSocket message router
   *
   * @param {Object} messageData - Raw WebSocket message
   *
   * Routes different message types to their specific handlers:
   * - 'new_message': Regular chat messages
   * - 'group_invitation': Incoming invitations
   * - 'group_invitation_response': Responses to our invitations
   */
  const handleIncomingWsResponse = messageData => {
    switch (messageData.type) {
      case 'new_message':
        handleIncomingMessage(messageData);
        break;
      case 'group_invitation':
        handleIncomingInvitation(messageData);
        break;
      case 'group_invitation_response':
        handleIncomingInvitationResponse(messageData);
        break;
      default:
        break;
    }
  };

  // ========== MESSAGE SENDING ==========
  /**
   * Sends a new message to the currently selected conversation
   *
   * @param {string} message - The message text to send
   *
   * Message Flow:
   * 1. Validates that a conversation is selected
   * 2. Generates unique message ID for duplicate prevention
   * 3. Creates and formats message object
   * 4. Updates Redux store immediately (optimistic update)
   * 5. Sends via WebSocket to other participants
   * 6. Updates conversation metadata
   */
  const addNewMessageToConversation = async message => {
    try {
      // Ensure we have a conversation selected
      if (!selectedConversationId) {
        return;
      }

      // Generate unique message ID and track for duplicate prevention
      const messageId = uuidv4();
      setLastSentMessageId(messageId);
      setLastSentMessageText(message);

      // Create formatted message object
      const newMessage = {
        id: messageId,
        text: message,
        date: new Date().toLocaleString([], {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }),
        sender: user.name,
        isCurrentUser: true,
      };

      // Optimistically add message to Redux store (immediate UI update)
      dispatch(
        addMessageToConversation({
          conversationId: selectedConversationId,
          message: newMessage,
        })
      );

      // Track pending messages for potential retry/error handling
      setPendingMessages(prev => [...prev, newMessage]);

      // Send message via WebSocket (only if not empty)
      if (newMessage.text.trim() !== '') {
        WebSocketService.sendMessage(newMessage.text, selectedConversationId);
      }

      // Update conversation's last message for sidebar display
      dispatch(
        updateConversationLastMessage({
          id: selectedConversationId,
          message: newMessage.text,
          time: newMessage.date,
        })
      );
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // ========== RENDER ==========
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <Grid container sx={{ height: '100%' }}>
        {isMobile ? (
          <>
            {/* ===== MOBILE / TABLET ===== */}
            <Grid item xs={12} sx={{ height: '100%' }}>
              {selectedConversation ? (
                <PageTransition
                  trigger={selectedConversation}
                  sx={{ height: '100% ' }}
                >
                  <ChatWindow onNewMessage={addNewMessageToConversation} />
                </PageTransition>
              ) : (
                <Sidebar
                  lastLocation={lastLocation}
                  chatRequests={chatRequests}
                  onRequestResponse={acceptRejectRequest}
                />
              )}
            </Grid>
          </>
        ) : (
          <>
            {/* ===== DESKTOP ===== */}
            <Grid item xs={12} md={4} lg={3} sx={{ height: '100%' }}>
              <Sidebar
                lastLocation={lastLocation}
                chatRequests={chatRequests}
                onRequestResponse={acceptRejectRequest}
              />
            </Grid>
            <Grid item xs={12} md={8} lg={9} sx={{ height: '100%' }}>
              {selectedConversation ? (
                // Show chat window when a conversation is selected
                <ChatWindow onNewMessage={addNewMessageToConversation} />
              ) : (
                // Show placeholder when no conversation is selected
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'gray',
                  }}
                >
                  <Typography variant="h6">
                    Select a conversation or start a new one
                  </Typography>
                </Box>
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default MessagingPage;
